/**
 * The screen-scoped parameter input context (KOA-17254).
 *
 * <b>Why this exists.</b> Resolving document parameters needed the screen author to bind the Resolve Document
 * Parameters result to every document consumer's `parameterContext` property by hand. That binding has exactly one
 * correct answer, so making it manual only created a step to forget — and forgetting it produced a document with its
 * `<TAG>` parameters showing, with nothing failing anywhere. A design-time warning for mandatory boilerplate is a
 * symptom, not a fix.
 *
 * So the action publishes its signed context here and every document consumer on that screen reads it. Nothing to
 * wire. The explicit `parameterContext` property stays available as an override for the case ambient cannot express:
 * two documents on one screen resolved for two different subjects.
 *
 * <b>Why it hangs off the screen instance and not a module-level map.</b> A single store keyed by screen id would
 * outlive navigation and leak one screen's context into the next visit; keying by instance means the value is
 * collected with the screen that owns it. It also keeps two tabs — or two mounted screens — independent, which is the
 * same reason the token is self-contained rather than a server-side session entry.
 */

/** Non-enumerable slot, so the context never shows up in state dumps, logs or serialised screen state. */
const AMBIENT_SLOT: unique symbol = Symbol("kuikaDocumentParameterContext");

interface IAmbientHost {
  [AMBIENT_SLOT]?: string;
}

/**
 * Publishes the signed context for a screen. Called by `resolveDocumentParameters` after the server signs the
 * inputs; the screen's own re-render is what makes consumers pick it up.
 */
export const setAmbientParameterContext = (screen: unknown, parameterContext: string | null | undefined): void => {
  if (!screen || typeof screen !== "object") return;

  const host: IAmbientHost = screen as IAmbientHost;
  const next: string | undefined = parameterContext || undefined;

  // Defined per assignment rather than once, because the value changes and a non-writable slot would throw on the
  // second resolve of the same screen.
  Object.defineProperty(host, AMBIENT_SLOT, {
    value: next,
    configurable: true,
    enumerable: false,
    writable: true
  });
};

/**
 * The context published for this screen, if any. Consumers walk up from themselves: a document component receives
 * its screen through the common props the generated screens pass down.
 */
export const getAmbientParameterContext = (screen: unknown): string | undefined => {
  if (!screen || typeof screen !== "object") return undefined;
  return (screen as IAmbientHost)[AMBIENT_SLOT];
};
